// Procedural WebAudio ambience for the Advent Calendar: a short looping motif
// per theme plus a themed "door open" sound. No audio assets required.
import { ADVENT_THEMES } from "@/lib/adventThemes";

let _ctx = null;
let _muted = false;

function ac() {
  if (!_ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
  }
  if (_ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
}

// Resume the context on the next user gesture (iOS Safari autoplay policy).
function unlockOnGesture() {
  const c = _ctx;
  if (!c || c.state === "running") return;
  const onGesture = () => {
    c.resume().catch(() => {});
    document.removeEventListener("pointerdown", onGesture, true);
    document.removeEventListener("touchend", onGesture, true);
  };
  document.addEventListener("pointerdown", onGesture, true);
  document.addEventListener("touchend", onGesture, true);
}

const midi = (m) => 440 * Math.pow(2, (m - 69) / 12);

// notes: [midi|0 (rest), beats]
const MOTIFS = {
  snow: {
    bpm: 126, wave: "triangle", decay: 0.55, vol: 0.05, pad: [60, 67],
    notes: [[76,1],[76,1],[76,2],[76,1],[76,1],[76,2],[76,1],[79,1],[72,1.5],[74,.5],[76,4],
            [77,1],[77,1],[77,1.5],[77,.5],[77,1],[76,1],[76,1],[76,.5],[76,.5],[76,1],[74,1],[74,1],[76,1],[74,2],[79,2],[0,2]],
  },
  stars: {
    bpm: 72, wave: "sine", decay: 1.3, vol: 0.05, pad: [48, 55],
    notes: [[72,1],[76,1],[79,1],[83,1],[86,1],[83,1],[79,1],[76,1],[81,1],[84,1],[88,1],[79,1],[77,1],[81,1],[84,1],[76,1],[0,2]],
  },
  autumn: {
    bpm: 96, wave: "triangle", decay: 0.7, vol: 0.045, pad: [45, 52],
    notes: [[69,1],[72,1],[76,1],[69,1],[72,1],[76,1],[67,1],[71,1],[74,1],[67,1],[71,1],[74,1],
            [65,1],[69,1],[72,1],[64,1],[67,1],[71,1],[69,3],[0,3]],
  },
  spring: {
    bpm: 132, wave: "triangle", decay: 0.28, vol: 0.05, pad: [50, 57],
    notes: [[74,1],[76,1],[78,1],[81,1],[83,1],[81,1],[78,1],[76,1],[74,1],[78,1],[81,1],[86,1],[83,1],[81,1],[78,2],[0,2]],
  },
  summer: {
    bpm: 118, wave: "sine", decay: 0.32, vol: 0.06, pad: [48, 55],
    notes: [[72,.5],[0,.5],[76,.5],[0,.5],[79,.5],[0,.5],[76,.5],[0,.5],[81,.5],[0,.5],[79,.5],[0,.5],[76,.5],[0,.5],[72,.5],[0,.5],
            [77,.5],[0,.5],[81,.5],[0,.5],[84,.5],[0,.5],[81,.5],[0,.5],[79,1],[76,1],[72,2],[0,2]],
  },
  valentine: {
    bpm: 66, wave: "sine", decay: 1.5, vol: 0.05, pad: [48, 55],
    notes: [[60,1],[64,1],[67,1],[71,1],[64,1],[67,1],[71,1],[74,1],[57,1],[60,1],[64,1],[67,1],[53,1],[57,1],[60,1],[64,1],[0,2]],
  },
  ramadan: {
    bpm: 80, wave: "sine", decay: 1.1, vol: 0.05, vibrato: true, pad: [50, 57],
    notes: [[62,1],[63,1],[66,1],[67,1],[69,1],[70,1],[69,1],[67,1],[66,1],[63,1],[62,2],[0,1],[69,1],[70,1],[72,1],[70,1],[69,2],[0,2]],
  },
  birthday: {
    bpm: 120, wave: "triangle", decay: 0.45, vol: 0.05, pad: [55, 62],
    notes: [[67,.75],[67,.25],[69,1],[67,1],[72,1],[71,2],[67,.75],[67,.25],[69,1],[67,1],[74,1],[72,2],
            [67,.75],[67,.25],[79,1],[76,1],[72,1],[71,1],[69,1],[77,.75],[77,.25],[76,1],[72,1],[74,1],[72,2],[0,2]],
  },
};

const _amb = { timer: null, master: null, pad: [], nextTime: 0, idx: 0, key: null };

function note(c, dest, freq, t0, dur, { wave = "triangle", vol = 0.05, decay = 0.5, vibrato = false } = {}) {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = wave;
  o.frequency.value = freq;
  if (vibrato) {
    const lfo = c.createOscillator();
    const lg = c.createGain();
    lfo.frequency.value = 5.5;
    lg.gain.value = freq * 0.006;
    lfo.connect(lg);
    lg.connect(o.frequency);
    lfo.start(t0);
    lfo.stop(t0 + dur + decay);
  }
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(vol, t0 + 0.015);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + Math.max(dur, 0.05) + decay);
  o.connect(g);
  g.connect(dest);
  o.start(t0);
  o.stop(t0 + dur + decay + 0.05);
}

export function startAmbient(themeKey) {
  stopAmbient();
  const m = MOTIFS[themeKey];
  if (!m || _muted) return;
  const c = ac();
  if (!c) return;
  unlockOnGesture();
  const master = c.createGain();
  master.gain.setValueAtTime(0.0001, c.currentTime);
  master.gain.linearRampToValueAtTime(1, c.currentTime + 1.2);
  master.connect(c.destination);
  // Soft sustained pad (two sines) under the motif.
  const pad = (m.pad || []).map((p) => {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "sine";
    o.frequency.value = midi(p);
    g.gain.value = 0.018;
    o.connect(g);
    g.connect(master);
    o.start();
    return o;
  });
  Object.assign(_amb, { master, pad, nextTime: c.currentTime + 0.1, idx: 0, key: themeKey });
  const beat = 60 / m.bpm;
  const tick = () => {
    if (_amb.master !== master) return;
    while (_amb.nextTime < c.currentTime + 0.4) {
      const [n, beats] = m.notes[_amb.idx % m.notes.length];
      const dur = beats * beat;
      if (n) note(c, master, midi(n), _amb.nextTime, dur * 0.9, m);
      _amb.nextTime += dur;
      _amb.idx += 1;
    }
  };
  tick();
  _amb.timer = setInterval(tick, 120);
}

export function stopAmbient() {
  if (_amb.timer) clearInterval(_amb.timer);
  _amb.timer = null;
  const { master, pad } = _amb;
  if (master && _ctx) {
    const t = _ctx.currentTime;
    try {
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.linearRampToValueAtTime(0.0001, t + 0.5);
    } catch {}
    setTimeout(() => {
      pad.forEach((o) => { try { o.stop(); } catch {} });
      try { master.disconnect(); } catch {}
    }, 600);
  }
  _amb.master = null;
  _amb.pad = [];
  _amb.key = null;
}

export function isAmbientPlaying() {
  return !!_amb.master;
}

// Lower the music while the result dialog is open.
export function duckAmbient(on) {
  const { master } = _amb;
  if (!master || !_ctx) return;
  const t = _ctx.currentTime;
  master.gain.cancelScheduledValues(t);
  master.gain.setValueAtTime(master.gain.value, t);
  master.gain.linearRampToValueAtTime(on ? 0.3 : 1, t + 0.4);
}

export function setAmbientMuted(v) {
  _muted = !!v;
  if (_muted) stopAmbient();
}

function noiseBurst(c, dest, t0, dur = 0.15, vol = 0.05) {
  const buf = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
  const s = c.createBufferSource();
  const g = c.createGain();
  const f = c.createBiquadFilter();
  f.type = "bandpass";
  f.frequency.value = 1800;
  s.buffer = buf;
  g.gain.value = vol;
  s.connect(f);
  f.connect(g);
  g.connect(dest);
  s.start(t0);
}

function sweep(c, dest, f0, f1, t0, dur, { wave = "sine", vol = 0.08 } = {}) {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = wave;
  o.frequency.setValueAtTime(f0, t0);
  o.frequency.exponentialRampToValueAtTime(f1, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(vol, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  o.connect(g);
  g.connect(dest);
  o.start(t0);
  o.stop(t0 + dur + 0.05);
}

// Themed door-open sound effect.
export function playDoorOpen(themeKey) {
  if (_muted) return;
  const c = ac();
  if (!c) return;
  const t = c.currentTime;
  const d = c.destination;
  const key = ADVENT_THEMES[themeKey] ? themeKey : "snow";
  try {
    noiseBurst(c, d, t, 0.12, 0.035);
    switch (key) {
      case "stars":
        sweep(c, d, 600, 2400, t + 0.05, 0.35, { vol: 0.07 });
        [84, 88, 91].forEach((n, i) => note(c, d, midi(n), t + 0.25 + i * 0.07, 0.1, { wave: "sine", vol: 0.06, decay: 0.8 }));
        break;
      case "autumn":
        note(c, d, 180, t, 0.05, { wave: "square", vol: 0.06, decay: 0.12 });
        note(c, d, 140, t + 0.09, 0.05, { wave: "square", vol: 0.05, decay: 0.15 });
        noiseBurst(c, d, t + 0.15, 0.25, 0.03);
        break;
      case "spring":
        [74, 78, 81].forEach((n, i) => note(c, d, midi(n), t + 0.05 + i * 0.06, 0.08, { wave: "triangle", vol: 0.07, decay: 0.35 }));
        break;
      case "summer":
        sweep(c, d, 300, 1400, t + 0.03, 0.14, { vol: 0.09 });
        sweep(c, d, 420, 1900, t + 0.2, 0.12, { vol: 0.06 });
        break;
      case "valentine":
        note(c, d, 70, t, 0.1, { wave: "sine", vol: 0.12, decay: 0.25 });
        note(c, d, 65, t + 0.28, 0.1, { wave: "sine", vol: 0.1, decay: 0.3 });
        [79, 83].forEach((n, i) => note(c, d, midi(n), t + 0.45 + i * 0.1, 0.1, { wave: "sine", vol: 0.05, decay: 0.9 }));
        break;
      case "ramadan":
        note(c, d, midi(50), t, 0.3, { wave: "sine", vol: 0.1, decay: 1.6 });
        note(c, d, midi(62), t + 0.02, 0.3, { wave: "sine", vol: 0.05, decay: 1.4 });
        note(c, d, midi(69), t + 0.05, 0.3, { wave: "sine", vol: 0.03, decay: 1.2 });
        break;
      case "birthday":
        sweep(c, d, 330, 520, t + 0.03, 0.28, { wave: "sawtooth", vol: 0.04 });
        noiseBurst(c, d, t + 0.3, 0.1, 0.06);
        [79, 84].forEach((n, i) => note(c, d, midi(n), t + 0.34 + i * 0.08, 0.1, { wave: "triangle", vol: 0.06, decay: 0.4 }));
        break;
      case "none":
        note(c, d, 900, t, 0.03, { wave: "square", vol: 0.04, decay: 0.05 });
        break;
      default: // snow — bell tinkle
        [88, 93, 96].forEach((n, i) => note(c, d, midi(n), t + 0.05 + i * 0.08, 0.08, { wave: "sine", vol: 0.06, decay: 0.9 }));
        note(c, d, midi(100), t + 0.32, 0.08, { wave: "sine", vol: 0.04, decay: 1.1 });
    }
  } catch {}
}
